import { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';

import type { PlumbingMaterial } from 'api';
import { useAnswerCodes } from '../hooks';

interface PlumbingMaterialsInputProps {
  value?: string;
  materials: PlumbingMaterial[];
  onChange: (materials: PlumbingMaterial[]) => void;
  otherPlumbingMaterialType: string;
  onOtherChange: (value: string) => void;
  isDisabled?: boolean;
  isFieldLocked?: (field: string) => boolean;
}

const PlumbingMaterialsInput = ({
  materials,
  onChange,
  otherPlumbingMaterialType,
  onOtherChange,
  isDisabled = false,
  isFieldLocked = () => false,
}: PlumbingMaterialsInputProps) => {
  const { getOptions, getCodeByLabel, shouldShowConditionalField } =
    useAnswerCodes();

  const plumbingOptions = useMemo(
    () => getOptions('plumbing_material_type'),
    [getOptions],
  );

  const otherCode = useMemo(
    () => getCodeByLabel('Other', 'plumbing_material_type'),
    [getCodeByLabel],
  );

  const showMultiple = materials.length > 1;

  const hasOtherSelected = useMemo(
    () => materials.some((m) => m.type === otherCode),
    [materials, otherCode],
  );

  const showOtherInput = useMemo(
    () =>
      hasOtherSelected ||
      (materials.length === 1 &&
        materials[0]?.type &&
        shouldShowConditionalField(
          'other_plumbing_material_type',
          materials[0].type,
        )),
    [materials, hasOtherSelected, shouldShowConditionalField],
  );

  const handleTypeChange = useCallback(
    (index: number, newType: string) => {
      const updated = [...materials];
      updated[index] = {
        ...updated[index],
        type: newType,
        percentage: showMultiple ? updated[index].percentage : null,
      };
      onChange(updated);

      if (newType !== otherCode && !updated.some((m) => m.type === otherCode)) {
        onOtherChange('');
      }
    },
    [materials, onChange, onOtherChange, showMultiple],
  );

  const handlePercentageChange = useCallback(
    (index: number, value: string) => {
      const numValue = value === '' ? null : parseInt(value, 10);
      if (
        numValue !== null &&
        (isNaN(numValue) || numValue < 0 || numValue > 100)
      ) {
        return;
      }

      const updated = [...materials];
      updated[index] = { ...updated[index], percentage: numValue };

      if (index < updated.length - 1 && numValue !== null) {
        const sumOthers = updated
          .slice(0, -1)
          .reduce(
            (sum, m, i) => sum + (i === index ? numValue : (m.percentage ?? 0)),
            0,
          );
        const remaining = Math.max(0, 100 - sumOthers);
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          percentage: remaining,
        };
      }

      onChange(updated);
    },
    [materials, onChange],
  );

  const handleAddMaterial = useCallback(() => {
    const currentSum = materials.reduce(
      (sum, m) => sum + (m.percentage ?? 0),
      0,
    );
    const remaining = Math.max(0, 100 - currentSum);

    const updated = materials.map((m, i) => ({
      ...m,
      percentage: i === 0 && m.percentage === null ? 50 : m.percentage,
    }));

    const firstPercentage = updated[0]?.percentage ?? 50;
    if (updated.length === 1) {
      updated[0] = { ...updated[0], percentage: firstPercentage };
    }

    const newRemaining =
      updated.length === 1 ? 100 - firstPercentage : remaining;

    onChange([...updated, { type: '', percentage: newRemaining }]);
  }, [materials, onChange]);

  const handleRemoveMaterial = useCallback(
    (index: number) => {
      const updated = materials.filter((_, i) => i !== index);

      if (updated.length === 1) {
        updated[0] = { ...updated[0], percentage: null };
      } else if (updated.length > 1) {
        const sumOthers = updated
          .slice(0, -1)
          .reduce((sum, m) => sum + (m.percentage ?? 0), 0);
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          percentage: Math.max(0, 100 - sumOthers),
        };
      }

      onChange(updated);

      const removedType = materials[index]?.type;
      if (
        removedType === otherCode &&
        !updated.some((m) => m.type === otherCode)
      ) {
        onOtherChange('');
      }
    },
    [materials, onChange, onOtherChange],
  );

  const getAvailableOptions = useCallback(
    (currentIndex: number) => {
      const selectedTypes = materials
        .map((m, i) => (i !== currentIndex ? m.type : null))
        .filter(Boolean);
      return plumbingOptions.filter(
        (opt) => !selectedTypes.includes(opt.value) || opt.value === '',
      );
    },
    [materials, plumbingOptions],
  );

  const isTypeLocked = isFieldLocked('plumbing_material_type');
  const isPercentageLocked = isFieldLocked('plumbing_material_percentage');

  return (
    <VStack spacing={2} align="stretch">
      {materials.map((material, index) => (
        <VStack key={index} spacing={1} align="stretch">
          <HStack spacing={1}>
            <Select
              flex={1}
              variant="main"
              size="sm"
              value={material.type}
              onChange={(e) => handleTypeChange(index, e.target.value)}
              isDisabled={isDisabled || isTypeLocked}
            >
              {getAvailableOptions(index).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            {showMultiple && (
              <IconButton
                aria-label="Remove plumbing type"
                icon={<CloseIcon boxSize={2} />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => handleRemoveMaterial(index)}
                isDisabled={isDisabled || materials.length <= 1}
              />
            )}
          </HStack>

          {showMultiple && (
            <Input
              variant="main"
              size="xs"
              type="number"
              min={0}
              max={100}
              value={material.percentage ?? ''}
              onChange={(e) => handlePercentageChange(index, e.target.value)}
              isDisabled={
                isDisabled ||
                isPercentageLocked ||
                index === materials.length - 1
              }
              placeholder="%"
            />
          )}
        </VStack>
      ))}

      {materials.length < 2 && (
        <Button
          size="xs"
          leftIcon={<AddIcon boxSize={2} />}
          onClick={handleAddMaterial}
          isDisabled={isDisabled || isTypeLocked}
          alignSelf="flex-start"
          fontSize="xs"
          px={2}
        >
          Add Type
        </Button>
      )}

      {showOtherInput && (
        <Box>
          <Text fontSize="xs" color="gray.600" mb={1}>
            Specify Other Material
          </Text>
          <Input
            variant="main"
            size="sm"
            value={otherPlumbingMaterialType}
            onChange={(e) => onOtherChange(e.target.value)}
            isDisabled={isDisabled}
          />
        </Box>
      )}
    </VStack>
  );
};

export default PlumbingMaterialsInput;
